import * as React from 'react';
import { CreditCard, ExternalLink } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

export interface PaymentMethod {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

interface PaymentMethodCardProps {
  paymentMethod: PaymentMethod | null;
  isLoading?: boolean;
}

export function PaymentMethodCard({
  paymentMethod,
  isLoading,
}: PaymentMethodCardProps): React.JSX.Element {
  const [isUpdating, setIsUpdating] = React.useState(false);

  const handleUpdatePaymentMethod = async (): Promise<void> => {
    setIsUpdating(true);
    try {
      const response = await api.post<{ url: string }>('/billing/portal', {
        returnUrl: window.location.href,
      });

      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      setIsUpdating(false);
    }
  };

  const getBrandIcon = (brand: string): string => {
    const brandLower = brand.toLowerCase();
    if (brandLower === 'visa') return 'Visa';
    if (brandLower === 'mastercard') return 'Mastercard';
    if (brandLower === 'amex') return 'American Express';
    return brand;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Payment Method</CardTitle>
          <CardDescription>Manage your payment information</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground">Loading payment method...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Payment Method</CardTitle>
        <CardDescription>Manage your payment information</CardDescription>
      </CardHeader>
      <CardContent>
        {paymentMethod ? (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 border rounded-lg">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-medium">
                  {getBrandIcon(paymentMethod.brand)} ending in {paymentMethod.last4}
                </p>
                <p className="text-sm text-muted-foreground">
                  Expires {paymentMethod.expMonth}/{paymentMethod.expYear}
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              onClick={handleUpdatePaymentMethod}
              disabled={isUpdating}
              className="w-full"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              {isUpdating ? 'Opening...' : 'Update Payment Method'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-muted-foreground">No payment method on file</p>
            <Button
              variant="outline"
              onClick={handleUpdatePaymentMethod}
              disabled={isUpdating}
              className="w-full"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              {isUpdating ? 'Opening...' : 'Add Payment Method'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
